import {
  HearingTaskProgress,
  UNKNOWN_TASK_PROGRESS,
} from './hearing-task-progress';
import { Injectable, Injector, computed, effect, signal } from '@angular/core';
import { Params } from '@angular/router';
import { Subject } from 'rxjs';
import { NavItemConfig } from '@app/core/models/nav-item.model';
import { NavContextItemRegistry } from '@app/core/services/nav-context-item-registry.service';
import {
  HEARING_DETAIL_ROUTE_BASE,
  HEARING_DETAIL_ROUTE_PATTERN,
  HearingCycleView,
  HearingPhase,
  hearingPhasePath,
} from '../navigation/phms-detail-navigation';
import { PHMS_HEARING_TERMINOLOGY } from '../phms-hearing.terminology';

export const HEARING_NAVIGATION = [
  {
    view: 'pre',
    path: 'pre-hearing',
    label: 'Pre-Hearing',
    sections: [
      { id: 'case-review', label: 'Verify history & risk' },
      { id: 'victim-notification', label: 'Participants & notification' },
      { id: 'analysis-notes', label: 'Prepare private notes' },
    ],
  },
  {
    view: 'at',
    path: 'at-hearing',
    label: 'At-Hearing',
    sections: [
      { id: 'hearing-record', label: 'Record participation & discussion' },
      { id: 'decision', label: 'Record decision' },
    ],
  },
  {
    view: 'post',
    path: 'post-hearing',
    label: 'Post-Hearing',
    sections: [{ id: 'decision', label: 'Complete legal review' }],
  },
] as const;

export const HEARING_SHARED_NAVIGATION = [
  { id: 'sdmf', label: 'SDMF Analysis' },
  { id: 'documents', label: 'Documents' },
  { id: 'activity', label: 'Activity' },
] as const;
export type HearingSharedView =
  (typeof HEARING_SHARED_NAVIGATION)[number]['id'];
export type HearingNavigationView = HearingCycleView | HearingSharedView;

export function isSharedHearingView(
  value: unknown,
): value is HearingSharedView {
  return value === 'documents' || value === 'activity' || value === 'sdmf';
}

export interface HearingNavigationContext {
  proceedingId: number;
  phase: HearingNavigationView;
  queryParams: Params;
  state?: Record<string, unknown>;
}

/** Source key under which the hearing shell registers its app-rail item (#1530). */
export const HEARING_RAIL_SOURCE_KEY = 'hearing';

/** Material icon shown on the rail's Hearing item. */
export const HEARING_RAIL_ICON = 'gavel';

/**
 * The app-rail Hearing item for one open proceeding (#1530 AC4): a flyout parent whose three
 * children route to that proceeding's phase pages. Labels come from the hearing-rail terminology
 * usages; `displayName` carries each usage's fallback text as the stable test hook while the rail
 * resolves the rendered text from `terminology`. Access derives from the hearing-detail route's
 * declared permissions through `accessRoute`.
 */
export function hearingRailItem(proceedingId: number): NavItemConfig {
  const text = PHMS_HEARING_TERMINOLOGY.hearingRail;
  const phaseLink = (
    phase: HearingPhase,
    usage: typeof text.phasePre,
    testId: string,
  ): NavItemConfig => ({
    displayName: usage.fallbackText,
    terminology: usage,
    route: hearingPhasePath(proceedingId, phase),
    testId,
  });

  return {
    displayName: text.item.fallbackText,
    terminology: text.item,
    iconName: HEARING_RAIL_ICON,
    route: `${HEARING_DETAIL_ROUTE_BASE}/${proceedingId}`,
    accessRoute: HEARING_DETAIL_ROUTE_PATTERN,
    presentation: 'flyout',
    testId: 'hearing-rail',
    children: [
      phaseLink('pre', text.phasePre, 'hearing-rail-pre'),
      phaseLink('at', text.phaseAt, 'hearing-rail-at'),
      phaseLink('post', text.phasePost, 'hearing-rail-post'),
    ],
  };
}

/** Shares section routing between the hearing shell's nested menu and its workspace. */
@Injectable({ providedIn: 'root' })
export class HearingNavigationService {
  readonly context = signal<HearingNavigationContext | null>(null);
  readonly currentWorkflowPhase = signal<string | null>(null);
  readonly progress = signal<Record<string, HearingTaskProgress>>({});
  readonly closeoutEvidence = signal<{
    proceedingId: number;
    closed: boolean | null;
  } | null>(null);
  readonly legalReviewEvidence = signal<{
    proceedingId: number;
    statusCode: string;
  } | null>(null);
  readonly structuredAnalysisEnabled = signal(false);
  readonly privateNotesAllowed = signal(false);
  readonly sectionRequested = new Subject<string>();
  readonly active = computed(() => this.context() !== null);
  readonly sharedItems = computed(() => HEARING_SHARED_NAVIGATION);
  readonly groups = computed(() =>
    HEARING_NAVIGATION.map((group) => ({
      ...group,
      sections: group.sections.filter(
        (section) =>
          group.view !== 'pre' ||
          section.id !== 'analysis-notes' ||
          this.privateNotesAllowed(),
      ),
    })),
  );
  readonly section = computed(() => {
    const context = this.context();
    if (isSharedHearingView(context?.phase)) return context.phase;
    const group = this.groups().find(
      (candidate) => candidate.view === context?.phase,
    );
    const requested = context?.queryParams['section'];
    if (context?.phase === 'pre' && requested === 'people-plans')
      return 'case-review';
    if (
      context?.phase === 'post' &&
      ['actions', 'closeout'].includes(requested)
    )
      return 'decision';
    if (
      context?.phase === 'at' &&
      ['participants', 'assessment-plans'].includes(requested)
    )
      return 'hearing-record';
    if (context?.phase === 'post' && requested === 'legal') return 'decision';
    if (group?.sections.some((section) => section.id === requested))
      return requested as string;
    if (
      context?.phase === 'post' &&
      !requested &&
      context.queryParams['legalReview']
    )
      return 'decision';
    return group?.sections[0]?.id ?? '';
  });
  readonly activeSection = computed(() =>
    this.section() === 'closeout' ? 'actions' : this.section(),
  );
  readonly sectionLabel = computed(
    () =>
      HEARING_SHARED_NAVIGATION.find(
        (item) => item.id === this.context()?.phase,
      )?.label ??
      this.groups()
        .find((group) => group.view === this.context()?.phase)
        ?.sections.find((section) => section.id === this.activeSection())
        ?.label ??
      'Hearing Cycle',
  );

  /** Proceeding whose rail item is currently registered, so phase-only context changes do not re-register it. */
  private railProceedingId: number | null = null;

  /**
   * The rail registry and injector arrive through constructor parameters (rather than `inject()`)
   * so the service stays constructible with `new` in isolated specs; without them the rail item is
   * simply never registered. With them, the rail item follows `context` (#1530 AC4): it appears
   * when the hearing shell sets a proceeding, re-points at a newly opened proceeding, and leaves
   * with `clear()`.
   */
  constructor(
    private readonly railItems?: NavContextItemRegistry,
    injector?: Injector,
  ) {
    if (this.railItems && injector) {
      effect(() => this.syncRailItem(), { injector });
    }
  }

  taskProgress(phase: string, section: string): HearingTaskProgress {
    return this.progress()[`${phase}/${section}`] ?? UNKNOWN_TASK_PROGRESS;
  }

  selectSection(section: string): void {
    if (this.active()) this.sectionRequested.next(section);
  }

  clear(): void {
    this.context.set(null);
    this.progress.set({});
    this.legalReviewEvidence.set(null);
    this.closeoutEvidence.set(null);
    this.currentWorkflowPhase.set(null);
    this.railProceedingId = null;
    this.railItems?.clear(HEARING_RAIL_SOURCE_KEY);
  }

  private syncRailItem(): void {
    const proceedingId = this.context()?.proceedingId ?? null;
    if (proceedingId === this.railProceedingId) return;
    this.railProceedingId = proceedingId;
    if (proceedingId === null) {
      this.railItems?.clear(HEARING_RAIL_SOURCE_KEY);
    } else {
      this.railItems?.set(HEARING_RAIL_SOURCE_KEY, [
        hearingRailItem(proceedingId),
      ]);
    }
  }
}
